import { Extension } from "@tiptap/core";

/**
 * TipTap extension that adds a `lineHeight` attribute to paragraph nodes.
 * Applies line-height classes (lh-tight, lh-normal, lh-loose) defined in
 * common/sections/richtext.css.
 *
 * This is a block-level attribute (not a mark) since line height applies
 * to entire paragraphs, not inline spans.
 */

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    lineHeight: {
      setLineHeight: (height: string) => ReturnType;
      unsetLineHeight: () => ReturnType;
    };
  }
}

const VALID_HEIGHTS = ["tight", "normal", "loose"];

export const LineHeightExtension = Extension.create({
  name: "lineHeight",

  addGlobalAttributes() {
    return [
      {
        types: ["paragraph"],
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: (element: HTMLElement) => {
              const classes = element.className?.split(" ") || [];
              const lhClass = classes.find((c) =>
                /^lh-(tight|normal|loose)$/.test(c),
              );
              return lhClass ? lhClass.replace("lh-", "") : null;
            },
            renderHTML: (attributes) => {
              if (
                !attributes.lineHeight ||
                !VALID_HEIGHTS.includes(attributes.lineHeight)
              ) {
                return {};
              }
              return { class: `lh-${attributes.lineHeight}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setLineHeight:
        (height: string) =>
        ({ commands }) => {
          if (!VALID_HEIGHTS.includes(height)) return false;
          return commands.updateAttributes("paragraph", {
            lineHeight: height,
          });
        },
      unsetLineHeight:
        () =>
        ({ commands }) => {
          return commands.updateAttributes("paragraph", {
            lineHeight: null,
          });
        },
    };
  },
});
